import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import AccessDenied from './AccessDenied';

interface PermissionProtectedRouteProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallbackMessage?: string;
}

export default function PermissionProtectedRoute({
  children,
  permission,
  permissions = [],
  requireAll = false,
  fallbackMessage,
}: PermissionProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const {
    hasPermission,
    hasAnyPermission,
    loading: permissionsLoading,
    isAdmin,
  } = usePermissions();
  const location = useLocation();

  // Show loading while checking authentication or permissions
  if (isLoading || permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin users can access everything
  if (isAdmin) {
    return <>{children}</>;
  }

  // Check permissions if specified
  if (permission || permissions.length > 0) {
    let hasAccess = false;

    if (permission) {
      hasAccess = hasPermission(permission);
    } else if (permissions.length > 0) {
      if (requireAll) {
        hasAccess = permissions.every(perm => hasPermission(perm));
      } else {
        hasAccess = hasAnyPermission(permissions);
      }
    }

    if (!hasAccess) {
      return (
        <AccessDenied
          message={
            fallbackMessage ||
            'Anda tidak memiliki akses untuk halaman ini. Hubungi administrator untuk mendapatkan akses.'
          }
        />
      );
    }
  }

  return <>{children}</>;
}
